import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { map, Observable, of, startWith, Subscription, take, timeout } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { ButtonMode, ButtonTheme, IconButtonSize, IconButtonTheme, NotificationTheme } from '@fcom/ui-components';
import { pathIsWithinHardcodedPath, unsubscribe } from '@fcom/core/utils';
import { AppState, CountryState, NativeBridgeService, RootPaths, WindowRef } from '@fcom/core';
import { akamaiCountry } from '@fcom/core/selectors';
import { finShare } from '@fcom/rx';

import { SelectOption } from '../../interfaces';
import { FinnairTheme } from '../../interfaces/theme.interface';
import { FinnairThemeService } from '../../services';
import { getCountryStates, resolveDetectedSelection } from '../../utils/language.utils';

export interface CountryAndLanguageSelection {
  country: string;
  languageCode: string;
}

interface CountryAndLanguageSelectionForm {
  country: FormControl<string>;
  language: FormControl<string>;
}

@Component({
  selector: 'fin-country-and-language-selector',
  templateUrl: './country-and-language-selector.component.html',
  styleUrls: ['country-and-language-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CountryAndLanguageSelectorComponent implements OnInit, OnDestroy {
  private fb = inject(UntypedFormBuilder);
  private languageService = inject(LanguageService);
  private router = inject(Router);
  private nativeBridge = inject(NativeBridgeService);
  private themeService = inject(FinnairThemeService);
  private store$ = inject<Store<AppState>>(Store);
  private windowRef = inject(WindowRef);

  static readonly GEOIP_PREFILL_TIMEOUT_MS = 2000;

  readonly isNewBrand = computed(() => {
    return this.themeService.siteTheme() !== FinnairTheme.LEGACY;
  });

  readonly titleText = computed(() => {
    return this.isNewBrand() ? "Just two things and you're on board" : 'Hi! Please set your location and language.';
  });

  readonly applyButtonText = computed(() => {
    if (this.buttonText()) {
      return this.buttonText();
    } else {
      return this.isNewBrand() ? "Let's go" : 'Apply preferences';
    }
  });

  readonly countrySelectLabel = computed(() => {
    return this.isNewBrand() ? 'Select or start typing' : undefined;
  });

  readonly languageSelectLabel = computed(() => {
    return this.isNewBrand() ? 'Select a language' : 'Select';
  });

  readonly ButtonMode = ButtonMode;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ButtonTheme = ButtonTheme;

  readonly useH1 = input(false);
  readonly prefill = input(true);
  readonly hideLanguageOptions = input(false);
  readonly lockLanguageSelection = input(false);
  readonly hideTitle = input(false);
  readonly showCloseButton = input(true);
  readonly fullWidthSubmit = input(true);
  readonly buttonText = input('Apply preferences');

  readonly applySelection = output<CountryAndLanguageSelection>();
  readonly closeClick = output<void>();

  subscriptions: Subscription = new Subscription();
  countries: CountryState[] = getCountryStates();

  countryOptions: SelectOption[];
  formGroup: FormGroup<CountryAndLanguageSelectionForm>;
  readonly showFlag = signal(false);

  languageOptions$: Observable<SelectOption[]>;
  readonly countryShopInfo = signal('');
  readonly countryShopWarning = signal('');

  ngOnInit(): void {
    this.countryOptions = this.countries.map((country) => {
      const { name, code, currencyCode, currencySymbol } = country;
      const optionName = currencyCode ? `${name} (${currencyCode} ${currencySymbol})` : name;
      return { name: optionName, value: code };
    });

    const langValue = this.languageService.langValue === 'master-en' ? 'gb-en' : this.languageService.langValue;

    const country = langValue?.includes('-') ? langValue.split('-')?.[0] : 'int';

    const localeIsGeneric = country === 'int';
    const applyGeoipPrefill = this.prefill() && localeIsGeneric;
    const initialCountry = this.prefill() && !applyGeoipPrefill ? country.toUpperCase() : '';

    this.formGroup = this.fb.group({
      country: this.fb.control(initialCountry, Validators.required),
      language: this.fb.control('', Validators.required),
    });

    this.languageOptions$ = this.formGroup.controls.country.valueChanges.pipe(
      startWith(initialCountry),
      map(
        (countryCode) =>
          this.countries
            .find((country) => country.code === countryCode)
            ?.languages.map((countryLanguage) => ({ name: countryLanguage.name, value: countryLanguage.code })) || []
      ),
      finShare()
    );

    this.formGroup.valueChanges.subscribe((newSelection) => {
      const countryCode = newSelection.country;
      this.countryShopInfo.set('');
      this.countryShopWarning.set('');
      const selectedCountry = this.countries.find((country) => country.code === countryCode);
      const countryChanged = countryCode !== country.toUpperCase();
      const languageChanged = newSelection.language && newSelection.language !== langValue;
      const countryOrLanguageChanged = countryChanged || languageChanged;
      const isInBooking = pathIsWithinHardcodedPath(this.router.url, RootPaths.BOOKING_ROOT);
      if (selectedCountry && !selectedCountry.currencyCode) {
        this.countryShopInfo.set('Prices on this site are in the currency of the country of departure.');
      }
      if (countryOrLanguageChanged && isInBooking && !this.nativeBridge.isInsideNativeWebView) {
        this.countryShopWarning.set(
          'Changing these preferences will reload this page, and you will lose any input information.'
        );
      }
    });

    this.subscriptions.add(
      this.languageOptions$.subscribe((options) => {
        if (!options || options.length === 0) {
          this.formGroup.controls.language.disable();
        } else {
          this.formGroup.controls.language.enable();
          const matchingValue = options.find((option) => option.value === langValue);

          if (matchingValue) {
            this.formGroup.controls.language.setValue(matchingValue.value);
          } else if (options.length === 1) {
            this.formGroup.controls.language.setValue(options[0].value);
          }

          if (this.lockLanguageSelection()) {
            const finnishOption = options.find((option) => option.value.endsWith('-fi'));
            const englishOption = options.find((option) => option.value === 'en' || option.value.endsWith('-en'));
            this.formGroup.controls.language.setValue(finnishOption?.value ?? englishOption.value);
          }

          if (options.length === 1 || this.lockLanguageSelection()) {
            this.formGroup.controls.language.disable();
          }

          this.showFlag.set(true);
        }
      })
    );

    if (applyGeoipPrefill) {
      this.subscriptions.add(
        this.store$
          .pipe(
            akamaiCountry(),
            take(1),
            timeout({
              each: CountryAndLanguageSelectorComponent.GEOIP_PREFILL_TIMEOUT_MS,
              with: () => of(undefined),
            }),
            map((countryCode) =>
              resolveDetectedSelection(countryCode, this.windowRef.nativeWindow?.navigator?.language, this.countries)
            )
          )
          .subscribe((selection) => {
            this.formGroup.controls.country.setValue(selection.country);
            this.formGroup.controls.language.setValue(selection.languageCode);
            this.showFlag.set(true);
          })
      );
    }
  }

  apply(): void {
    const countryCode = this.formGroup.controls.country.value;
    const selectedCountry = this.countries.find((country) => country.code === countryCode);
    const languageCode = this.getSelectedLanguage(selectedCountry);
    this.applySelection.emit({ country: countryCode, languageCode });
  }

  updateShowFlag(dropDownOpen: boolean): void {
    this.showFlag.set(!dropDownOpen && Boolean(this.formGroup.controls.country.value));
  }

  emitClose(): void {
    this.closeClick.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  private getSelectedLanguage(country: CountryState): string {
    if (!this.hideLanguageOptions()) {
      return this.formGroup.controls.language.value;
    }
    const sameLanguageInSelectedCountry = country.languages.find(
      (countryLanguage) => countryLanguage.ISOCode === this.languageService.langKeyValue
    );
    return sameLanguageInSelectedCountry?.code ?? country.languages[0].code;
  }
}
